import { useEffect, useState, type FormEvent } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Copy, Loader2, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  listDepartments,
  listMunicipalities,
} from "@facturar/locations";
import { isDianResponsibilityCode } from "@facturar/dian-types";
import { ApiError } from "@/lib/api";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCompany, useUpdateCompany } from "@/lib/api/companies.queries";
import { useCanWriteCompanies } from "@/lib/team-context";
import { translateCompanyError } from "@/components/companies/error-translations";
import { CompanyLogoCard } from "@/components/companies/CompanyLogoCard";
import type { UpdateCompanyInput } from "@/lib/api/companies";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/companies/$companyId/edit")({
  component: EditCompanyPage,
});

function EditCompanyPage() {
  return (
    <RequireAuth>
      <AppShell>
        <EditCompanyContent />
      </AppShell>
    </RequireAuth>
  );
}

const RESPONSIBILITIES = [
  { code: "R-99-PN", labelKey: "companies.responsibility.R-99-PN" },
  { code: "O-13", labelKey: "companies.responsibility.O-13" },
  { code: "O-15", labelKey: "companies.responsibility.O-15" },
  { code: "O-23", labelKey: "companies.responsibility.O-23" },
  { code: "O-47", labelKey: "companies.responsibility.O-47" },
] as const;

function EditCompanyContent() {
  const { companyId } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const companyQuery = useCompany(companyId);
  const updateCompany = useUpdateCompany(companyId);
  const canEdit = useCanWriteCompanies();

  const [legalName, setLegalName] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [departmentCode, setDepartmentCode] = useState("");
  const [cityCode, setCityCode] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [regimeCode, setRegimeCode] = useState<"48" | "49">("48");
  const [responsibilities, setResponsibilities] = useState<string[]>([]);
  const [isLargeTaxpayer, setIsLargeTaxpayer] = useState(false);
  const [isSelfWithholder, setIsSelfWithholder] = useState(false);
  const [radianAutoAcuse, setRadianAutoAcuse] = useState(false);
  // Firmante RADIAN (PF-339): IssuerParty/Person of every event AR.
  const [signatoryIdType, setSignatoryIdType] = useState<"CC" | "CE" | "PP">("CC");
  const [signatoryIdNumber, setSignatoryIdNumber] = useState("");
  const [signatoryFirstName, setSignatoryFirstName] = useState("");
  const [signatoryFamilyName, setSignatoryFamilyName] = useState("");
  const [signatoryJobTitle, setSignatoryJobTitle] = useState("");
  const [signatoryDepartment, setSignatoryDepartment] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (companyQuery.data) {
      const c = companyQuery.data;
      setLegalName(c.legalName);
      setTradeName(c.tradeName ?? "");
      setDepartmentCode(c.departmentCode ?? "");
      setCityCode(c.cityCode ?? "");
      setAddressLine1(c.addressLine1 ?? "");
      setAddressLine2(c.addressLine2 ?? "");
      setEmail(c.email ?? "");
      setPhone(c.phone ?? "");
      setRegimeCode((c.regimeCode as "48" | "49") ?? "48");
      // Rows written before the allowlist (or through a bypassing client) can
      // hold raw RUT codes the API rejects; seed only valid codes so the next
      // save heals the row instead of 400ing.
      setResponsibilities((c.responsibilities ?? []).filter(isDianResponsibilityCode));
      setIsLargeTaxpayer(c.isLargeTaxpayer);
      setIsSelfWithholder(c.isSelfWithholder);
      setRadianAutoAcuse(c.radianAutoAcuse);
      setSignatoryIdType(c.radianSignatory?.idType ?? "CC");
      setSignatoryIdNumber(c.radianSignatory?.idNumber ?? "");
      setSignatoryFirstName(c.radianSignatory?.firstName ?? "");
      setSignatoryFamilyName(c.radianSignatory?.familyName ?? "");
      setSignatoryJobTitle(c.radianSignatory?.jobTitle ?? "");
      setSignatoryDepartment(c.radianSignatory?.department ?? "");
    }
  }, [companyQuery.data]);

  const departments = listDepartments();
  const municipalities = departmentCode
    ? listMunicipalities(departmentCode)
    : [];

  function toggleResp(code: string) {
    setResponsibilities((prev) =>
      prev.includes(code) ? prev.filter((r) => r !== code) : [...prev, code],
    );
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    // Firmante RADIAN: id + names are the minimum DIAN accepts (AAH12–AAH16).
    // All-empty clears it; a partial fill is a user mistake — surface it.
    const signatoryFilled =
      signatoryIdNumber.trim() !== "" ||
      signatoryFirstName.trim() !== "" ||
      signatoryFamilyName.trim() !== "";
    if (
      signatoryFilled &&
      (signatoryIdNumber.trim() === "" ||
        signatoryFirstName.trim() === "" ||
        signatoryFamilyName.trim() === "")
    ) {
      setError(t("companies.radian.signatory_incomplete"));
      return;
    }

    const payload: UpdateCompanyInput = {
      legalName: legalName.trim(),
      tradeName: tradeName.trim() || null,
      cityCode: cityCode || null,
      departmentCode: departmentCode || null,
      addressLine1: addressLine1.trim() || null,
      addressLine2: addressLine2.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      regimeCode,
      responsibilities,
      isLargeTaxpayer,
      isSelfWithholder,
      radianAutoAcuse,
      radianSignatory: signatoryFilled
        ? {
            idType: signatoryIdType,
            idNumber: signatoryIdNumber.trim(),
            firstName: signatoryFirstName.trim(),
            familyName: signatoryFamilyName.trim(),
            ...(signatoryJobTitle.trim()
              ? { jobTitle: signatoryJobTitle.trim() }
              : {}),
            ...(signatoryDepartment.trim()
              ? { department: signatoryDepartment.trim() }
              : {}),
          }
        : null,
    };

    updateCompany.mutate(payload, {
      onSuccess: () => {
        toast.success(t("companies.edit.toast.success"));
        void navigate({
          to: "/companies/$companyId",
          params: { companyId },
        });
      },
      onError: (err) => {
        if (err instanceof ApiError && err.code) {
          const translated = translateCompanyError(err.code, t);
          toast.error(translated ?? err.message);
          return;
        }
        toast.error(err instanceof Error ? err.message : t("common.error"));
      },
    });
  }

  if (companyQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (companyQuery.isError || !companyQuery.data) {
    return (
      <div className="mx-auto max-w-3xl pt-6">
        <ErrorState
          title={t("companies.detail.error.title")}
          description={t("companies.detail.error.body")}
          onRetry={() => void companyQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      </div>
    );
  }

  // The API refuses a viewer's PATCH; don't offer a form it can't save.
  if (!canEdit) {
    return (
      <div className="mx-auto max-w-3xl pt-6">
        <ErrorState
          title={t("companies.read_only.title")}
          description={t("companies.read_only.body")}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <div>
        <Link
          to="/companies/$companyId"
          params={{ companyId }}
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("companies.edit.back")}
        </Link>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>
            {t("companies.edit.title", { name: companyQuery.data.legalName })}
          </CardTitle>
          <CardDescription>{t("companies.edit.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4" onSubmit={onSubmit} noValidate>
            <div className="grid gap-1.5">
              <Label>{t("companies.create.field.legal_name")}</Label>
              <Input
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                autoComplete="organization"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("companies.create.field.trade_name")}</Label>
              <Input
                value={tradeName}
                onChange={(e) => setTradeName(e.target.value)}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label>{t("companies.create.field.department")}</Label>
                <Select
                  value={departmentCode || undefined}
                  onValueChange={(v) => {
                    setDepartmentCode(v);
                    setCityCode("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "companies.create.field.department_placeholder",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {departments.map((d) => (
                      <SelectItem key={d.code} value={d.code}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>{t("companies.create.field.city")}</Label>
                <Select
                  value={cityCode || undefined}
                  onValueChange={(v) => setCityCode(v)}
                  disabled={!departmentCode}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t("companies.create.field.city_placeholder")}
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {municipalities.map((m) => (
                      <SelectItem key={m.code} value={m.code}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>{t("companies.create.field.address_line1")}</Label>
              <Input
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("companies.create.field.address_line2")}</Label>
              <Input
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label>{t("companies.create.field.email")}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="grid gap-1.5">
                <Label>{t("companies.create.field.phone")}</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>{t("companies.create.field.regime")}</Label>
              <Select
                value={regimeCode}
                onValueChange={(v) => setRegimeCode(v as "48" | "49")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="48">{t("companies.regime.48")}</SelectItem>
                  <SelectItem value="49">{t("companies.regime.49")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>{t("companies.create.field.responsibilities")}</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {RESPONSIBILITIES.map((r) => {
                  const checked = responsibilities.includes(r.code);
                  return (
                    <label
                      key={r.code}
                      className={cn(
                        "flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm",
                        checked ? "border-primary bg-primary/5" : "",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleResp(r.code)}
                        className="mt-0.5 h-4 w-4 rounded border-input"
                      />
                      <span>
                        <span className="font-medium">{r.code}</span>{" "}
                        <span className="text-muted-foreground">
                          {t(r.labelKey)}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <label
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm",
                  isLargeTaxpayer ? "border-primary bg-primary/5" : "",
                )}
              >
                <input
                  type="checkbox"
                  checked={isLargeTaxpayer}
                  onChange={(e) => setIsLargeTaxpayer(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-input"
                />
                <span>{t("companies.flags.large_taxpayer")}</span>
              </label>
              <label
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm",
                  isSelfWithholder ? "border-primary bg-primary/5" : "",
                )}
              >
                <input
                  type="checkbox"
                  checked={isSelfWithholder}
                  onChange={(e) => setIsSelfWithholder(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-input"
                />
                <span>{t("companies.flags.self_withholder")}</span>
              </label>
            </div>

            {/* Recepción RADIAN (PF-339): per-company inbox + auto-acuse. */}
            <div className="grid gap-2 border-t pt-4">
              <Label>{t("companies.radian.section_title")}</Label>
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-muted px-2 py-1 font-mono text-xs">
                  {companyQuery.data.inboxEmail}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2"
                  onClick={() => {
                    void navigator.clipboard
                      .writeText(companyQuery.data!.inboxEmail)
                      .then(() =>
                        toast.success(t("companies.radian.inbox_copied")),
                      )
                      .catch(() => toast.error(t("companies.radian.copy_failed")));
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />
                  {t("companies.radian.copy_inbox")}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("companies.radian.inbox_hint")}
              </p>
              <label
                className={cn(
                  "flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm",
                  radianAutoAcuse ? "border-primary bg-primary/5" : "",
                )}
              >
                <input
                  type="checkbox"
                  checked={radianAutoAcuse}
                  onChange={(e) => setRadianAutoAcuse(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-input"
                />
                <span>
                  <span className="font-medium">
                    {t("companies.radian.auto_acuse_label")}
                  </span>{" "}
                  <span className="text-muted-foreground">
                    {t("companies.radian.auto_acuse_hint")}
                  </span>
                </span>
              </label>

              {/* Firmante de eventos RADIAN (PF-339): DIAN exige la persona
                  natural que firma cada evento (IssuerParty/Person,
                  AAH12–AAH16) — sin firmante los eventos quedan bloqueados. */}
              <div className="mt-2 grid gap-2">
                <Label>{t("companies.radian.signatory_title")}</Label>
                <p className="text-xs text-muted-foreground">
                  {t("companies.radian.signatory_hint")}
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label>{t("companies.radian.signatory_id_type")}</Label>
                    <Select
                      value={signatoryIdType}
                      onValueChange={(v) =>
                        setSignatoryIdType(v as "CC" | "CE" | "PP")
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CC">
                          {t("companies.radian.signatory_id_type_cc")}
                        </SelectItem>
                        <SelectItem value="CE">
                          {t("companies.radian.signatory_id_type_ce")}
                        </SelectItem>
                        <SelectItem value="PP">
                          {t("companies.radian.signatory_id_type_pp")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <Label>{t("companies.radian.signatory_id_number")}</Label>
                    <Input
                      value={signatoryIdNumber}
                      onChange={(e) => setSignatoryIdNumber(e.target.value)}
                      inputMode="numeric"
                    />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label>{t("companies.radian.signatory_first_name")}</Label>
                    <Input
                      value={signatoryFirstName}
                      onChange={(e) => setSignatoryFirstName(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label>{t("companies.radian.signatory_family_name")}</Label>
                    <Input
                      value={signatoryFamilyName}
                      onChange={(e) => setSignatoryFamilyName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label>{t("companies.radian.signatory_job_title")}</Label>
                    <Input
                      value={signatoryJobTitle}
                      onChange={(e) => setSignatoryJobTitle(e.target.value)}
                      placeholder={t(
                        "companies.radian.signatory_job_title_placeholder",
                      )}
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <Label>{t("companies.radian.signatory_department")}</Label>
                    <Input
                      value={signatoryDepartment}
                      onChange={(e) => setSignatoryDepartment(e.target.value)}
                      placeholder={t(
                        "companies.radian.signatory_department_placeholder",
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
            {error ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
                {error}
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button asChild type="button" variant="outline">
                <Link
                  to="/companies/$companyId"
                  params={{ companyId }}
                >
                  {t("common.cancel")}
                </Link>
              </Button>
              <Button type="submit" disabled={updateCompany.isPending}>
                {updateCompany.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {t("companies.edit.cta")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Marca (PF-335): the logo rendered in the invoice PDF header. */}
      <CompanyLogoCard companyId={companyId} company={companyQuery.data} />
    </div>
  );
}
